import React, { useState, useCallback, useEffect } from 'react';

import {
  Box,
  TextField,
  Grid,
  Typography,
  Switch,
  FormControlLabel,
  FormHelperText,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  MenuItem,
  Card,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  styled,
  Button,
  ButtonGroup,
  Menu,
  ListItemText,
} from '@mui/material';

import { grey, red } from '@mui/material/colors';

import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state';

import { useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonSectionHeader from '../../common/CommonSectionHeader';
import { CommonIcons, icons } from '../../common/CommonIcons';
import CommonRestricted from '../../common/CommonRestricted';

import { useItems, itemTypes, targetTypes } from '../../../hooks/api/useItems';
import { useSystemPrompt } from '../../../hooks/useSystemPrompt';
import { useAppContextFeatures } from '../../../context/AppContext';
import { useUsers, userTypes } from '../../../hooks/api/useUsers';
import { useCustomFields, customFieldFunctions, customFieldModules } from '../../../hooks/api/useCustomFields';
import { useSystemUtils } from '../../../hooks/useSystemUtils';
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';

import { checkValueExists } from '../../../utils/dataSchema';

import UpdateItem from '../items/UpdateItem';
import UpdateItemSettings from '../items/UpdateItemSettings';
import UpdateItemImages from '../items/UpdateItemImages';
import NewItem from '../items/NewItem';

import { initialValues, validationSchema } from '../../../schema/products/productSchema';

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
}));

export default function UpdateProduct({ routeType }) {
  const { id } = useParams();

  const { promptMessage } = useSystemPrompt();
  const { list: listSuppliers } = useUsers({ userType: userTypes.supplier });
  const {
    isLoading,
    list: listItems,
    update: updateItem,
    get: getItem,
    remove: removeItems,
  } = useItems({
    itemType: itemTypes.product,
    targetType: targetTypes.product,
  });
  const {
    list: listCustomFields,
    filterOperatorTypes,
  } = useCustomFields({
    customFieldModule: customFieldModules.item,
    customFieldFunction: customFieldFunctions.item.item,
  });

  const { setSystemLoading, openCustomDrawer, closeCustomDrawer } = useAppContextFeatures();
  const { isPermissionAllow } = usePermissions(routeType, scopeIds.product.id, scopeTypes.product.product.id);

  const { currencyAbb } = useSystemUtils();
  const { getHtmlHead } = useSystemHead(routeType);
  const { getRoute } = useSystemRoutes(routeType);

  const [savedValues, setSavedValues] = useState();
  const [data, setData] = useState();
  const [dataUoms, setDataUoms] = useState();
  const [dataSuppliers, setDataSuppliers] = useState();
  const [dataBrands, setDataBrands] = useState([]);
  const [dataCustomFields, setDataCustomFields] = useState([]);

  const [category, setCategories] = useState({
    level0: [],
    level1: [],
    level2: [],
  });

  const onSubmit = async (currentValues) => {
    setSystemLoading(true);

    try {
      let categoryId = '';

      if (currentValues.categoryLevel2Id !== '') {
        categoryId = `${currentValues.categoryLevel2Id}`;
      } else if (currentValues.categoryLevel1Id !== '') {
        categoryId = `${currentValues.categoryLevel1Id}`;
      } else if (currentValues.categoryLevel0Id !== '') {
        categoryId = `${currentValues.categoryLevel0Id}`;
      }

      let data = {
        product: {
          item_name: currentValues?.name ? currentValues.name : '',
          model: currentValues?.model ? currentValues.model : '',
          product_code: currentValues?.productCode,
          description: currentValues?.description ? currentValues.description : '',
          category_id: categoryId,
          brand_id: currentValues?.brandId,
          summary: currentValues?.summary ? currentValues.summary : '',
          status: currentValues?.status ? 1 : 0,
        },
        props: {
          default_uom_item_id: currentValues?.uomId,
        },
      };

      const response = await updateItem({ id, data });

      promptMessage(true, `${response?.message}`);
      // navigate(getRoute("products"))

      loadData(id);
    } catch (error) {
      promptMessage(false, `Action Failed (${error?.message})`);
    }

    setSystemLoading(false);
  };

  const formik = useFormik({
    initialValues: savedValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit,
  });

  const { values, errors, touched, handleChange, setFieldValue } = formik;

  const loadData = useCallback(
    async (id) => {
      setSystemLoading(true);

      try {
        let props = {
          id,
        };

        props.dataTypesArray = ['all_category'];

        const { data } = await getItem(props);
        const { product, items } = data;

        // check the category id is level 0 or 1 or 2
        const { all_category: allCategory } = product;

        let c0Id = '';
        let c1Id = '';
        let c2Id = '';

        if (allCategory.length) {
          // check for level = 2
          const level2 = allCategory.filter((i) => i.level === 2);
          c2Id = level2 && level2.length > 0 ? level2[0].id : '';

          // check for level = 1
          const level1 = allCategory.filter((i) => i.level === 1);
          c1Id = level1 && level1.length > 0 ? level1[0].id : '';

          // check for level = 0
          const level0 = allCategory.filter((i) => i.level === 0);
          c0Id = level0 && level0.length > 0 ? level0[0].id : '';
        }

        if (c0Id) {
          fetchDataCategory({ level: 1, parentId: c0Id });
        }

        if (c1Id) {
          fetchDataCategory({ level: 2, parentId: c1Id });
        }

        setSavedValues({
          name: product?.item_name ? product.item_name : '',
          description: product?.description ? product.description : '',
          model: product?.model ? product.model : '',
          productCode: product?.product_code,
          summary: product?.summary ? product.summary : '',
          uomId: product?.default_uom_item_id ? product.default_uom_item_id : '',
          brandId: product?.brand_id,
          // categoryId,
          status: product?.status === '1',
          categoryLevel0Id: c0Id,
          categoryLevel1Id: c1Id,
          categoryLevel2Id: c2Id,
          // secondCateId: "0",
          // tertiaryCateId: "0"
        });

        setData(data);
      } catch (error) {
        promptMessage(false, `${error?.message}`);
      }

      setSystemLoading(false);
    },
    [id]
  );

  const fetchDataCategory = async ({ level, parentId }) => {
    try {
      const props = {
        limit: -1,
        skip: 0,
        filterValue: [],
        sortInfo: {
          id: 'name',
          dir: 0,
        },
      };

      if (parentId) {
        props.filterValue = [
          {
            name: 'parent_category_id',
            operator: filterOperatorTypes.eq,
            value: parentId,
          },
        ];
      }

      // for the first level category
      if (level === 0) {
        props.filterValue = [
          {
            name: 'parent_category_id',
            operator: filterOperatorTypes.eq,
            value: '0',
          },
        ];
      }

      props.itemType = targetTypes.category;

      const { data } = await listItems(props);

      if (level === 0) {
        setCategories((prev) => ({ ...prev, level0: data?.item_categories }));
      } else if (level === 1) {
        setCategories((prev) => ({ ...prev, level1: data?.item_categories }));
      } else if (level === 2) {
        setCategories((prev) => ({ ...prev, level2: data?.item_categories }));
      }
    } catch (error) {
      promptMessage(false, `${error?.message}`);
    }
  };

  const fetchCustomFields = async () => {
    setSystemLoading(true);

    try {
      const props = {
        limit: -1,
        skip: 0,
        sortInfo: {
          id: 'id',
          dir: 0,
        },
        filterValue: [
          {
            name: 'field_module',
            operator: filterOperatorTypes.eq,
            value: customFieldModules.item,
          },
          {
            name: 'module_function',
            operator: filterOperatorTypes.eq,
            value: customFieldFunctions.item.item,
          },
        ],
      };

      const response = await listCustomFields(props);

      setDataCustomFields(response?.data?.custom_fields);

      // promptMessage(true, `${response?.message}`)
    } catch (error) {
      promptMessage(false, `Action Failed (${error?.message})`);
    }

    setSystemLoading(false);
  };

  const fetchUoms = async () => {
    setSystemLoading(true);

    try {
      const props = {
        limit: -1,
        skip: 0,
        filterValue: [],
        sortInfo: {
          id: 'name',
          dir: 0,
        },
      };

      props.itemType = targetTypes.uom;

      const result = await listItems(props);

      setDataUoms(result?.data?.item_uoms);
    } catch (error) {
      promptMessage(false, `Action Failed (${error?.message})`);
    }

    setSystemLoading(false);
  };

  const fetchSuppliers = async () => {
    setSystemLoading(true);

    try {
      const props = {
        limit: -1,
        skip: 0,
        filterValue: [],
        sortInfo: {
          id: 'username',
          dir: 0,
        },
      };

      const result = await listSuppliers(props);

      setDataSuppliers(result?.data?.suppliers);
    } catch (error) {
      promptMessage(false, `Action Failed (${error?.message})`);
    }

    setSystemLoading(false);
  };

  const fetchBrands = async () => {
    setSystemLoading(true);

    try {
      const props = {
        limit: -1,
        skip: 0,
        filterValue: [],
        sortInfo: {
          id: 'name',
          dir: 0,
        },
      };

      props.itemType = targetTypes.brand;

      const result = await listItems(props);

      setDataBrands(result?.data?.item_brands);
    } catch (error) {
      promptMessage(false, `Action Failed (${error?.message})`);
    }

    setSystemLoading(false);
  };

  useEffect(() => {
    loadData(id);
    fetchUoms();
    fetchSuppliers();
    fetchBrands();
    fetchDataCategory({ level: 0 });
    fetchCustomFields();
  }, []);

  const handleRemoveItem = async ({ id: uomId }) => {
    try {
      // check the uomid deleted cannot same with default uom
      if (uomId === values.uomId) {
        promptMessage(false, `Cannot remove the defaut uom item`);
        return false;
      }

      const response = await removeItems({ ids: [uomId], itemType: itemTypes.item });

      promptMessage(true, `${response?.message}`);

      loadData(id);
    } catch (error) {
      promptMessage(false, `Action Failed (${error?.message})`);
    }

    return true;
  };

  const RenderInfoForm = () => {
    if (!values) return <></>;

    const { items: uoms } = data;

    return (
      <form onSubmit={formik.handleSubmit}>
        <Box>
          <StyledCard>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6} md={6} textAlign={'left'}>
                <CommonSectionHeader title={`Information`} subTitle={`Detail information described the products`} />
              </Grid>

              <Grid item xs={12} sm={6} md={6} textAlign={'right'}>
                <FormControlLabel
                  sx={{
                    marginLeft: '1px',
                    my: 1,
                  }}
                  control={
                    <Switch
                      name="status"
                      checked={Boolean(values?.status)}
                      onChange={(event, checked) => {
                        setFieldValue('status', checked);
                      }}
                    />
                  }
                  label={Boolean(values?.status) ? 'Enabled' : 'Disabled'}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12}>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={12} md={8}>
                    <TextField
                      margin="dense"
                      size="small"
                      // autoFocus
                      fullWidth
                      name={'name'}
                      label="Name"
                      type="text"
                      variant="outlined"
                      value={values?.name}
                      onChange={handleChange}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                    />
                  </Grid>

                  <Grid item xs={12} sm={12} md={4}>
                    <TextField
                      margin="dense"
                      size="small"
                      fullWidth
                      name={'model'}
                      label="Model"
                      type="text"
                      variant="outlined"
                      value={values?.model ? values.model : ''}
                      onChange={handleChange}
                      error={touched.model && Boolean(errors.model)}
                      helperText={touched.model && errors.model}
                    />
                  </Grid>

                  <Grid item xs={6} sm={6} md={8}>
                    <FormControl
                      margin="dense"
                      fullWidth
                      size="small"
                      error={touched.brandId && Boolean(errors.brandId)}
                    >
                      <InputLabel id="uomId">Default Uom</InputLabel>
                      <Select
                        labelId="uomId"
                        label="Default Uom"
                        name={'uomId'}
                        onChange={handleChange}
                        value={
                          // checkExists({ dataSearch: dataBrands, fieldSearch: "id", valueToCompare: values?.uomId })
                          checkValueExists({
                            searchFieldId: 'id',
                            dataSearch: uoms,
                            valueToCompare: values?.uomId,
                          })
                        }
                        sx={{
                          textAlign: 'left',
                        }}
                      >
                        {uoms &&
                          uoms?.map((item, index) => (
                            <MenuItem key={index} value={item?.id}>
                              {item.code} ({item?.uom_name})
                            </MenuItem>
                          ))}
                      </Select>
                      <FormHelperText>{touched.uomId && errors.uomId}</FormHelperText>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={12} md={4}>
                    <TextField
                      margin="dense"
                      size="small"
                      fullWidth
                      name={'productCode'}
                      label="Product Code"
                      type="text"
                      variant="outlined"
                      value={values?.productCode ? values.productCode : ''}
                      onChange={handleChange}
                      error={touched.productCode && Boolean(errors.productCode)}
                      helperText={touched.productCode && errors.productCode}
                    />
                  </Grid>

                  <Grid item xs={12} sm={12} md={12}>
                    <TextField
                      margin="dense"
                      size="small"
                      fullWidth
                      name={'summary'}
                      label="Summary"
                      type="text"
                      variant="outlined"
                      multiline
                      rows={3}
                      value={values?.summary}
                      onChange={handleChange}
                      error={touched.summary && Boolean(errors.summary)}
                      helperText={touched.summary && errors.summary}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={12}>
                    <TextField
                      margin="dense"
                      size="small"
                      fullWidth
                      name={'description'}
                      label="Description"
                      type="text"
                      variant="outlined"
                      multiline
                      rows={3}
                      value={values?.description}
                      onChange={handleChange}
                      error={touched.description && Boolean(errors.description)}
                      helperText={touched.description && errors.description}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={6}>
                    <FormControl
                      margin="dense"
                      fullWidth
                      size="small"
                      error={touched.brandId && Boolean(errors.brandId)}
                    >
                      <InputLabel id="brandId">Brand</InputLabel>
                      <Select
                        labelId="brandId"
                        label="Brand"
                        name={'brandId'}
                        onChange={handleChange}
                        value={checkValueExists({
                          searchFieldId: 'id',
                          dataSearch: dataBrands,
                          valueToCompare: values?.brandId,
                        })}
                        sx={{
                          textAlign: 'left',
                        }}
                      >
                        {dataBrands &&
                          dataBrands?.map((item, index) => (
                            <MenuItem key={index} value={item?.id}>
                              {item.name}
                            </MenuItem>
                          ))}
                      </Select>
                      <FormHelperText>{touched.brandId && errors.brandId}</FormHelperText>
                    </FormControl>
                  </Grid>

                  {/* <Grid item xs={12} sm={12} md={6}>
                                        <FormControl
                                            margin="dense"
                                            fullWidth
                                            size="small"
                                            error={touched.categoryId && Boolean(errors.categoryId)}
                                        >
                                        <InputLabel id="categoryId">Category</InputLabel>
                                            <Select
                                                labelId="categoryId"
                                                label="Category"
                                                name={"categoryId"}
                                                onChange={handleChange}
                                                value={
                                                    checkValueExists({
                                                        searchFieldId: "category_id",
                                                        dataSearch: dataCategories,
                                                        valueToCompare: values?.categoryId
                                                    })
                                                }
                                                sx={{
                                                    textAlign: 'left'
                                                }}
                                            >

                                                {
                                                    dataCategories && dataCategories?.map((item, index) =>
                                                        <MenuItem key={index} value={item?.category_id}>{item.name}</MenuItem>
                                                    )
                                                }

                                            </Select>
                                            <FormHelperText>
                                                {touched.categoryId && errors.categoryId}
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid> */}

                  <Grid item xs={12} sm={12} md={6}>
                    <FormControl
                      margin="dense"
                      fullWidth
                      size="small"
                      error={touched.categoryLevel0Id && Boolean(errors.categoryLevel0Id)}
                    >
                      <InputLabel id="categoryLevel0Id">Category #1</InputLabel>
                      <Select
                        labelId="categoryLevel0Id"
                        label="Category"
                        name={'categoryLevel0Id'}
                        onChange={(event) => {
                          setFieldValue('categoryLevel0Id', event.target.value);

                          fetchDataCategory({ level: 1, parentId: event.target.value });

                          // clear the child
                          setCategories((prev) => ({ ...prev, level1: [], level2: [] }));
                          setFieldValue('categoryLevel1Id', '');
                          setFieldValue('categoryLevel2Id', '');
                        }}
                        value={checkValueExists({
                          searchFieldId: 'id',
                          dataSearch: category.level0,
                          valueToCompare: values?.categoryLevel0Id,
                        })}
                        sx={{
                          textAlign: 'left',
                        }}
                      >
                        {category?.level0 &&
                          category.level0?.map((i, index) => {
                            return (
                              <MenuItem key={index} value={i.id}>
                                {i.name}
                              </MenuItem>
                            );
                          })}
                      </Select>
                      <FormHelperText>{touched.categoryLevel0Id && errors.categoryLevel0Id}</FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={12} md={6}>
                    <FormControl
                      margin="dense"
                      fullWidth
                      size="small"
                      error={touched.categoryLevel1Id && Boolean(errors.categoryLevel1Id)}
                    >
                      <InputLabel id="categoryLevel1Id">Category #2</InputLabel>
                      <Select
                        labelId="categoryLevel1Id"
                        label="Category"
                        name={'categoryLevel1Id'}
                        onChange={(event) => {
                          setFieldValue('categoryLevel1Id', event.target.value);

                          fetchDataCategory({ level: 2, parentId: event.target.value });

                          // clear the child
                          setCategories((prev) => ({ ...prev, level2: [] }));
                          setFieldValue('categoryLevel2Id', '');
                        }}
                        value={checkValueExists({
                          searchFieldId: 'id',
                          dataSearch: category.level1,
                          valueToCompare: values?.categoryLevel1Id,
                        })}
                        sx={{
                          textAlign: 'left',
                        }}
                      >
                        {category?.level1 &&
                          category.level1?.map((i, index) => {
                            return (
                              <MenuItem key={index} value={i.id}>
                                {i.name}
                              </MenuItem>
                            );
                          })}
                      </Select>
                      <FormHelperText>{touched.categoryLevel1Id && errors.categoryLevel1Id}</FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={12} md={6}>
                    <FormControl
                      margin="dense"
                      fullWidth
                      size="small"
                      error={touched.categoryLevel2Id && Boolean(errors.categoryLevel2Id)}
                    >
                      <InputLabel id="categoryLevel2Id">Category #3</InputLabel>
                      <Select
                        labelId="categoryLevel2Id"
                        label="Category"
                        name={'categoryLevel2Id'}
                        onChange={(event) => {
                          setFieldValue('categoryLevel2Id', event.target.value);
                        }}
                        value={checkValueExists({
                          searchFieldId: 'id',
                          dataSearch: category.level2,
                          valueToCompare: values?.categoryLevel2Id,
                        })}
                        sx={{
                          textAlign: 'left',
                        }}
                      >
                        {category?.level2 &&
                          category.level2?.map((i, index) => {
                            return (
                              <MenuItem key={index} value={i.id}>
                                {i.name}
                              </MenuItem>
                            );
                          })}
                      </Select>
                      <FormHelperText>{touched.categoryLevel2Id && errors.categoryLevel2Id}</FormHelperText>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </StyledCard>

          <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
            <Grid item md={12} textAlign={'right'}>
              <Button
                type="submit"
                // fullWidth
                variant="contained"
                sx={{
                  mt: 3,
                  mb: 2,
                }}
              // loading={isLoading}
              >
                Save Changes
              </Button>
            </Grid>
          </Grid>
        </Box>
      </form>
    );
  };

  const RenderEmptyUom = () => {
    return (
      <Box
        sx={{
          p: 10,
        }}
        textAlign={'center'}
      >
        <CommonIcons
          name={icons.blank}
          sx={{
            // fontSize: '5rem',
            width: 50,
            height: 50,
            color: grey[400],
          }}
        />
      </Box>
    );
  };

  const RenderUomList = ({ uoms }) => {
    if (!data?.items) return <></>;
    if (data.items.length <= 0) return <></>;

    return (
      <List
        sx={{
          // display: 'flex',
          // flexDirection: 'column',
          // flex: 1,
          // flexGrow: 1,
          // maxHeight: 500,
          minHeight: 500,
          // backgroundColor: 'red',
          // alignItems: 'flex-start',
          // justifyContent: 'flex-start',
          // overflow: "hidden",
          // overflowY: "scroll",
        }}
      >
        {data.items.map((item, index) => {
          const values = data.items.filter((i) => i.id === item.id);

          if (values.length <= 0) return <></>;

          return <RenderUomFormRow key={index} item={item} />;
        })}
      </List>
    );
  };

  const RenderUomForm = () => {
    if (!data?.items) return <></>;

    const { items: uoms } = data;

    return (
      <StyledCard>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={12} md={12} textAlign={'left'}>
            <CommonSectionHeader title={`Saved Uoms`} subTitle={`Showing total ${uoms.length} variants`} />
          </Grid>
          <Grid item md={12} xs={12}>
            {uoms && uoms.length ? <RenderUomList routeType={routeType} uoms={uoms} /> : <RenderEmptyUom />}

            {isPermissionAllow({ scopeType: scopeTypes.product.item.id, functionType: functionTypes.pageNew.id }) && (
              <Button
                color={'primary'}
                fullWidth
                variant="outlined"
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                onClick={() => {
                  openCustomDrawer({
                    show: true,
                    anchor: 'right',
                    title: `New Uom`,
                    PaperProps: {
                      sx: { width: 4 / 5 },
                    },
                    handleOnClose: () => { },
                    ComponentActions: <></>,
                    ComponentBody: (
                      <NewItem
                        routeType={routeType}
                        // id={item.id}
                        productId={id}
                        // productValues={savedValues}
                        dataUoms={dataUoms}
                        dataSuppliers={dataSuppliers}
                        dataCustomFields={dataCustomFields}
                        // handleOnSuccess={() => {
                        //     // console.log('yesyes')
                        //     loadData(id);
                        // }}
                        // loadValues={loadValues}
                        // dataUoms={dataUoms}
                        // dataSuppliers={dataSuppliers}
                        handleOnSuccess={() => {
                          closeCustomDrawer();
                          loadData(id);
                        }}
                      />
                    ),
                  });
                }}
              >
                <CommonIcons name={icons.add} />
                <Box sx={{ ml: 1 }}>New Uom</Box>
              </Button>
            )}
          </Grid>
        </Grid>
      </StyledCard>
    );
  };

  const RenderUomFormRowActions = ({ id, item, dataUoms, dataSuppliers }) => {
    // check need to show the button menu or not if all permission disallowed
    // if(
    //     isPermissionAllow({ scopeType: scopeTypes.product.item.id, functionType: functionTypes.pageUpdate.id }) === false &&
    //     isPermissionAllow({ scopeType: scopeTypes.product.item.id, functionType: functionTypes.pageImages.id }) === false &&
    //     isPermissionAllow({ scopeType: scopeTypes.product.item.id, functionType: functionTypes.pageSettings.id }) === false &&
    //     isPermissionAllow({ scopeType: scopeTypes.product.item.id, functionType: functionTypes.remove.id }) === false
    // ){
    //     return <></>
    // }

    return (
      <PopupState variant="popover" popupId="commonBreadcumbsActionMenu">
        {(popupState) => (
          <>
            <ButtonGroup variant="outline">
              <Button {...bindTrigger(popupState)} size="small" color={'primary'}>
                <CommonIcons name={icons.morevert} />
              </Button>
            </ButtonGroup>

            <Menu {...bindMenu(popupState)}>
              {
                // isPermissionAllow({ scopeType: scopeTypes.product.item.id, functionType: functionTypes.pageUpdate.id }) &&

                <MenuItem
                  dense
                  sx={
                    {
                      // m: 1,
                    }
                  }
                  onClick={() => {
                    popupState.close();

                    openCustomDrawer({
                      show: true,
                      anchor: 'right',
                      title: `Update Information`,
                      PaperProps: {
                        sx: { width: 4 / 5 },
                      },
                      handleOnClose: () => { },
                      ComponentActions: <></>,
                      ComponentBody: (
                        <UpdateItem
                          routeType={routeType}
                          id={item.id}
                          dataUoms={dataUoms}
                          dataSuppliers={dataSuppliers}
                          // dataCustomFields={dataCustomFields}
                          handleOnSuccess={() => {
                            closeCustomDrawer();
                            loadData(id);
                          }}
                        />
                      ),
                    });
                  }}
                >
                  <ListItemText>Edit</ListItemText>
                </MenuItem>
              }

              {
                // isPermissionAllow({ scopeType: scopeTypes.product.item.id, functionType: functionTypes.pageImages.id }) &&

                <MenuItem
                  dense
                  sx={
                    {
                      // m: 1,
                    }
                  }
                  onClick={() => {
                    popupState.close();

                    openCustomDrawer({
                      show: true,
                      anchor: 'right',
                      title: `Update Images`,
                      PaperProps: {
                        sx: { width: 4 / 5 },
                      },
                      handleOnClose: () => { },
                      ComponentActions: <></>,
                      ComponentBody: (
                        <UpdateItemImages
                          routeType={routeType}
                          id={item.id}
                          handleOnSuccess={() => {
                            closeCustomDrawer();
                            loadData(id);
                          }}
                        />
                      ),
                    });
                  }}
                >
                  <ListItemText>Images</ListItemText>
                </MenuItem>
              }

              {
                // isPermissionAllow({ scopeType: scopeTypes.product.item.id, functionType: functionTypes.pageSettings.id }) &&

                <MenuItem
                  dense
                  sx={
                    {
                      // m: 1,
                    }
                  }
                  onClick={() => {
                    popupState.close();

                    openCustomDrawer({
                      show: true,
                      anchor: 'right',
                      title: `Settings`,
                      PaperProps: {
                        sx: { width: 4 / 5 },
                      },
                      handleOnClose: () => { },
                      ComponentActions: <></>,
                      ComponentBody: (
                        <UpdateItemSettings
                          routeType={routeType}
                          id={item.id}
                          handleOnSuccess={() => {
                            closeCustomDrawer();
                            loadData(id);
                          }}
                        />
                      ),
                    });
                  }}
                >
                  <ListItemText>Settings</ListItemText>
                </MenuItem>
              }

              {isPermissionAllow({ scopeType: scopeTypes.product.item.id, functionType: functionTypes.remove.id }) && (
                <MenuItem
                  dense
                  sx={
                    {
                      // m: 1,
                    }
                  }
                  onClick={() => {
                    popupState.close();

                    handleRemoveItem({ id: item.id });
                  }}
                >
                  <ListItemText>Remove</ListItemText>
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </PopupState>
    );
  };

  const RenderUomFormRow = ({ item }) => {
    return (
      <ListItem
        // key={index}
        disablePadding
        divider
      >
        <ListItemButton
          role={undefined}
          sx={{
            p: 1,
          }}
          // onClick={() => {
          //     openView({ data: item, open: true, id: 'viewUom' })
          // }}
          dense
        >
          {/* {item?.id} */}
          <Grid container spacing={1}>
            <Grid item md={10} xs={6} sm={6} flexGrow>
              <Typography
                sx={{
                  fontSize: '1rem',
                  fontWeight: 800,
                }}
                display={'flex'}
                flexDirection={'row'}
                justifyContent={'flex-start'}
                alignItems={'center'}
              >
                {item?.uom_name}
                {item?.status === '0' && <CommonIcons name={icons.disabled} sx={{ color: red[600] }} />}

                {item?.is_package === '1' && <CommonIcons name={icons.products} />}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  textOverflow: 'ellipsis',
                }}
              >
                {item?.code}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  textOverflow: 'ellipsis',
                }}
              >
                {currencyAbb} {item?.uom_price}
              </Typography>
            </Grid>
            <Grid item md={2} xs={3} sm={3} textAlign={'center'}>
              <RenderUomFormRowActions id={id} item={item} dataUoms={dataUoms} dataSuppliers={dataSuppliers} />
            </Grid>
          </Grid>
        </ListItemButton>
      </ListItem>
    );
  };

  const RenderForm = () => {
    return (
      <Grid container spacing={1} textAlign={'left'}>
        <Grid item md={7} xs={12}>
          {RenderInfoForm()}
        </Grid>
        <Grid item md={5} xs={12}>
          {RenderUomForm()}
        </Grid>
      </Grid>
    );
  };

  return (
    <Box>
      {getHtmlHead({ routeType, title: headTitles.updateItem })}

      <CommonBreadcumbs
        routeType={routeType}
        pageTitle="Update Item"
        breadcrumbs={[
          {
            displayText: 'Items',
            props: {
              href: getRoute('products/items'),
            },
          },
          {
            displayText: 'Update Item',
            props: {
              color: 'secondary.main',
            },
          },
        ]}
      />

      {isPermissionAllow({ functionType: functionTypes.pageUpdate.id }) ? RenderForm() : <CommonRestricted />}
    </Box>
  );
}
